import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';

/**
 * Modern Mobile Bottom Navigation Bar (Frosted Glass & Pill Highlights)
 */
const MobileBottomBar = ({
  items = [],
  currentPath = '',
  onNavigate,
  onToggleMenu,
  themeColor,
  isMenuOpen = false
}) => {
  const theme = useTheme();
  
  // Show top 4 items on bottom bar + Menu button
  const primaryItems = items.slice(0, 4);
  const activeColor = themeColor || theme.palette.primary.main;
  
  const isPrimaryActive = (path) => currentPath === path;
  const isOtherActive = items.slice(4).some(item => currentPath === item.path);

  return (
    <Paper
      elevation={8}
      className="mobile-bottom-nav"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        display: { xs: 'flex', md: 'none' },
        borderTop: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.06)',
        justifyContent: 'space-around',
        alignItems: 'center',
        py: 0.6,
        px: 1,
        height: 64,
        WebkitBackdropFilter: 'blur(16px)',
        backdropFilter: 'blur(16px)',
        bgcolor: 'rgba(255, 255, 255, 0.94)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      {primaryItems.map((item) => {
        const active = isPrimaryActive(item.path);
        return (
          <Box
            key={item.path}
            onClick={() => onNavigate && onNavigate(item.path)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              cursor: 'pointer',
              color: active ? activeColor : '#64748b',
              py: 0.4,
              px: 0.5,
              borderRadius: 2,
              transition: 'all 0.15s ease-in-out',
              userSelect: 'none',
              '&:active': { transform: 'scale(0.92)' }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                px: 1.5,
                py: 0.35,
                borderRadius: 4,
                bgcolor: active ? 'rgba(5, 150, 105, 0.12)' : 'transparent',
                transition: 'all 0.2s',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.35rem',
                  color: active ? activeColor : '#64748b'
                }
              }}
            >
              {item.icon}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6875rem',
                fontWeight: active ? 700 : 500,
                color: 'inherit',
                mt: 0.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                lineHeight: 1.1
              }}
            >
              {item.name}
            </Typography>
          </Box>
        );
      })}

      {/* Menu / More button */}
      <Box
        onClick={onToggleMenu}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          cursor: 'pointer',
          color: (isMenuOpen || isOtherActive) ? activeColor : '#64748b',
          py: 0.4,
          px: 0.5,
          borderRadius: 2,
          transition: 'all 0.15s ease-in-out',
          userSelect: 'none',
          '&:active': { transform: 'scale(0.92)' }
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            px: 1.5,
            py: 0.35,
            borderRadius: 4,
            bgcolor: (isMenuOpen || isOtherActive) ? 'rgba(5, 150, 105, 0.12)' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          <MenuIcon 
            sx={{ 
              fontSize: '1.35rem',
              color: (isMenuOpen || isOtherActive) ? activeColor : '#64748b'
            }} 
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.6875rem',
            fontWeight: (isMenuOpen || isOtherActive) ? 700 : 500,
            color: 'inherit',
            mt: 0.2,
            lineHeight: 1.1
          }}
        >
          Menu
        </Typography>
      </Box>
    </Paper>
  );
};

export default MobileBottomBar;
